'use client';

import { type AlertColor, Button, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
	DataGridPremium,
	GridPaginationModel,
	GridRowSelectionModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
} from '@mui/x-data-grid-premium';
import { useMutation, useQuery } from '@parkchirp/api-client';
import { FC, ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { ACTIVATE_SELECTED_PARKERS, DELETE_PENDING_PARKERS_BY_SUBSCRIPTION } from '../../mutations';
import { GET_WAITLIST_PARKERS_CSV, LIST_WAITLISTED_PARKERS } from '../../queries';
import type { SetShowDialog, SetShowSnackbar, SnackbarParams } from '../../types';
import { datagridFilterPanelAttributes, dayjs } from '../../utils';
import { isOwnerPortal } from '../../utils/constants';
import { Snackbar } from '../alerts';
import { FacilityAutocomplete } from '../inputs';
import { DatagridLinearProgress } from '../loading';
import { ConfirmationModal, Dialog } from '../modals';
import { CsvExportButtonV2 } from '../reports';
import {
	defaultFilters,
	defaultOrder,
	getWaitlistParkersTableColumns,
	OnFilterModelChange,
	OnSortModelChange,
} from './waitlistParkersTableConfig';
import { WaitListStartDateAndPriceModal } from './waitlistStartDateAndPriceModal';

type WaitlistParkersTableProps = {
	csvExportButton: ((props: any) => ReactElement) | null;
	parkerDetailModal: ((props: any) => ReactElement) | null;
	subscriptionId: string;
	setShowDialog: SetShowDialog;
	showDialog?: ReactNode | null;
	showSnackbar?: Partial<SnackbarParams>;
	setShowSnackbar?: SetShowSnackbar;
	tenantSubscriptionId?: string;
	usesDefaultRates?: boolean;
	theme: any;
};

export const WaitlistParkersTable: FC<WaitlistParkersTableProps> = ({
	csvExportButton,
	parkerDetailModal,
	subscriptionId,
	setShowSnackbar = null,
	setShowDialog = null,
	showDialog = null,
	showSnackbar = null,
	tenantSubscriptionId,
	usesDefaultRates = false,
	theme,
}) => {
	const subIdToUse = subscriptionId || tenantSubscriptionId;
	const [sortOrder, setSortOrder] = useState<{ field: string; sort: string }>(defaultOrder);
	const [filters, setFilters] = useState<Array<any>>(defaultFilters);
	const debouncedSetFilters = useDebounce(setFilters, 500);

	const [{ pageSize, page }, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [selectedParkerIds, setSelectedParkerIds] = useState<GridRowSelectionModel>([]);
	const [rowCount, setRowCount] = useState<number>(0);
	const [showSnackbarForParkersTable, setShowSnackbarForParkersTable = setShowSnackbar] = useState(showSnackbar);
	const [showDialogForParkersTable, setShowDialogForParkersTable = setShowDialog] = useState(showDialog);

	const [bulkParkerActionName, setBulkParkerActionName] = useState<any>(null);
	const [parkersWithErrors, setParkersWithErrors] = useState([]);
	const [activateParkersFacility, setActivateParkersFacility] = useState<{
		id?: string;
		facilityName?: string;
		facilityNumber?: string;
	}>({});

	const [activateSelectedParkers, { loading: activateSelectedParkersLoading }] =
		useMutation(ACTIVATE_SELECTED_PARKERS);

	const [deletePendingParkersBySubscription, { loading: deletePendingParkersBySubscriptionLoading }] = useMutation(
		DELETE_PENDING_PARKERS_BY_SUBSCRIPTION,
	);

	const {
		data: waitlistParkers,
		loading: waitlistParkersLoading,
		refetch,
	} = useQuery(LIST_WAITLISTED_PARKERS, {
		variables: {
			subscriptionId,
			limit: pageSize,
			offset: page * pageSize,
			order: sortOrder,
			filters,
			tenantSubscriptionId,
		},
		context: {
			apiVersion: 'v2',
		},
		fetchPolicy: 'network-only',
	});

	const parkerRows = useMemo(
		() => waitlistParkers?.listWaitlistedParkers?.parkers ?? [],
		[waitlistParkers?.listWaitlistedParkers?.parkers],
	);

	useEffect(() => {
		if (!waitlistParkersLoading && waitlistParkers) {
			setRowCount(waitlistParkers?.listWaitlistedParkers?.total);
		}
	}, [waitlistParkersLoading, waitlistParkers]);

	useEffect(() => {
		setRowCount((prevRowCountState) => (rowCount !== undefined ? rowCount : prevRowCountState));
	}, [rowCount]);

	const columns = getWaitlistParkersTableColumns({
		parkerDetailModal,
		setShowDialog,
	});

	const handleBulkActionChange = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setBulkParkerActionName(value);
	};

	const getStartDateAndPriceError = useCallback(
		({ monthlyFeePrice, parkerStartDate }) => {
			if (usesDefaultRates && !monthlyFeePrice) {
				return 'Please add a monthly fee price';
			}

			if (!parkerStartDate) {
				return 'Please add a parker start date';
			}

			return null;
		},
		[usesDefaultRates],
	);

	const CustomToolbar = useCallback(() => {
		const getAdditionalDataForId = (parkerIds) =>
			parkerIds.map((id) => ({
				id,
				subscriptionId: parkerRows.find((pr) => pr.id === id)?.subscriptionId,
			}));
		const getGroupedBySubscriptionId = () => {
			const parkersWithSubId = getAdditionalDataForId(selectedParkerIds);
			const groupedBySubscriptionId = parkersWithSubId.reduce((acc, obj) => {
				if (!acc[obj.subscriptionId]) {
					acc[obj.subscriptionId] = [];
				}
				acc[obj.subscriptionId].push(obj.id);
				return acc;
			}, {});
			return groupedBySubscriptionId;
		};
		const renderCorrectInputsFromAction = () => {
			const handleActionSubmitClick = async (
				clickedConfirm = false,
				newParkerStartDate = null,
				newMonthlyFeePrice = null,
			) => {
				if (!clickedConfirm && !bulkParkerActionName.includes('Remove')) {
					const handleStartDateAndPriceConfirm = ({ monthlyFeePrice, parkerStartDate }) => {
						const error = getStartDateAndPriceError({ monthlyFeePrice, parkerStartDate });

						if (error) {
							setShowSnackbar({
								severity: 'error',
								message: error,
							});
							return;
						}

						setShowDialog(null);
						handleActionSubmitClick(true, parkerStartDate, monthlyFeePrice);
					};

					setShowDialogForParkersTable(
						<WaitListStartDateAndPriceModal
							handleClose={() => setShowDialogForParkersTable(null)}
							handleConfirm={handleStartDateAndPriceConfirm}
							theme={theme}
							usesDefaultRates={usesDefaultRates}
						/>,
					);
					return;
				}
				if (!clickedConfirm && bulkParkerActionName.includes('Remove')) {
					setShowDialogForParkersTable(
						<ConfirmationModal
							title='Are you sure you want to remove the selected waitlist parker(s)?'
							content=''
							dismissText='Cancel'
							type='text'
							confirmText='Confirm'
							handleDismiss={() => setShowDialog(null)}
							handleConfirm={() => handleActionSubmitClick(true, null, null)}
						/>,
					);
					return;
				}
				let resultMessage = `Bulk action "${bulkParkerActionName}" successful`;
				let severity: AlertColor = 'success';
				let errors = [];

				try {
					setShowDialogForParkersTable(null);

					if (
						bulkParkerActionName === 'Activate Waitlist Parkers' ||
						bulkParkerActionName === 'Activate Waitlist Parkers By Facility'
					) {
						const { data } = await activateSelectedParkers({
							context: {
								apiVersion: 'v2',
								appName: process.env.NEXT_PUBLIC_APP_NAME,
							},
							variables: {
								parkerIds: selectedParkerIds,
								createBackBillInvoices: false,
								waitlist: true,
								monthlyFeePrice: newMonthlyFeePrice ? parseFloat(newMonthlyFeePrice) : null,
								parkerStartDate: newParkerStartDate
									? dayjs.utc(newParkerStartDate).format('YYYY-MM-DD')
									: null,
							},
						});
						const { errors: activationErrors } = data.activateSelectedParkers;

						errors = activationErrors.flatMap((activationError) => {
							let { errorMessage } = activationError;
							if (!errorMessage && activationError.parkersAfterLastStartDate?.length) {
								errorMessage = `Some parker's start dates are after the facility's last available billing start date`;
							}

							return [
								...(activationError.failedParkers.map((parkerId) => ({
									parkerId,
									errorMessage,
								})) ?? []),
								...(activationError.parkersAfterLastStartDate?.map((parkerId) => ({
									parkerId,
									errorMessage,
								})) ?? []),
							];
						});

						resultMessage = errors.length
							? 'Some of these parkers could not be activated'
							: 'Successfully activated waitlist parkers';
						severity = errors.length ? 'warning' : 'success';
					}
					if (
						bulkParkerActionName === 'Remove Waitlist Parkers' ||
						bulkParkerActionName === 'Remove Waitlist Parkers By Facility'
					) {
						const groupedData = getGroupedBySubscriptionId();

						const promises = Object.entries(groupedData).map(async ([subId, parkerIds]) =>
							deletePendingParkersBySubscription({
								context: {
									apiVersion: 'v2',
									appName: process.env.NEXT_PUBLIC_APP_NAME,
								},
								variables: {
									subscriptionId: subId,
									parkerIds,
									tenantSubscriptionId,
									waitlist: true,
								},
							}),
						);

						const results = await Promise.allSettled(promises);
						errors = results.flatMap((r: any) => {
							const { parkersWithErrors: errorParkers } = r.value.data.deletePendingParkersBySubscription;
							const { errorMessage = '' } = r.value.data.deletePendingParkersBySubscription;

							return errorParkers.map((parker: any) => ({
								...parker,
								errorMessage,
							}));
						});

						resultMessage = errors.length
							? 'Some of these parkers could not be deleted'
							: 'Successfully deleted waitlist parkers';
						severity = errors.length ? 'warning' : 'success';
					}
				} catch (e) {
					resultMessage = `Unable to ${bulkParkerActionName}: ${e.message}`;
					severity = 'error';
				} finally {
					const errorParkerIds = errors.filter((e) => e.parkerId).map((err) => err.parkerId);
					const errorMessage = errors.length > 0 && errors[0].errorMessage ? errors[0].errorMessage : '';
					refetch();
					setParkersWithErrors([...errorParkerIds]);
					setShowSnackbar({
						message: `${resultMessage}${errorMessage ? `: ${errorMessage}` : errorMessage}`,
						severity,
					});
				}
			};

			if (subIdToUse && bulkParkerActionName === 'Activate Waitlist Parkers') {
				return (
					<Button
						loading={activateSelectedParkersLoading}
						disabled={!parkerRows?.length || !selectedParkerIds.length}
						onClick={() => handleActionSubmitClick()}
						variant='contained'
						data-cy='activate-parkers-button'
					>
						Activate Selected Waitlist Parkers
					</Button>
				);
			}
			if (subIdToUse && bulkParkerActionName === 'Remove Waitlist Parkers') {
				return (
					<Button
						loading={deletePendingParkersBySubscriptionLoading}
						disabled={!parkerRows?.length || !selectedParkerIds.length}
						onClick={() => handleActionSubmitClick()}
						variant='contained'
						data-cy='activate-parkers-button'
					>
						Remove Selected Waitlist Parkers
					</Button>
				);
			}
			if (!subIdToUse && bulkParkerActionName === 'Activate Waitlist Parkers By Facility') {
				return (
					<>
						<div style={{ width: '25%' }}>
							<FacilityAutocomplete
								facilityName={activateParkersFacility.facilityName}
								facilityNumber={activateParkersFacility.facilityNumber}
								facilityId={activateParkersFacility.id}
								organizationId={0}
								onChange={(selectedFacility) => {
									setActivateParkersFacility({
										id: selectedFacility?.id ?? '',
										facilityName: selectedFacility?.facilityName ?? '',
										facilityNumber: selectedFacility?.facilityNumber?.toString(),
									});

									debouncedSetFilters([
										...filters,
										{
											propertyName: 'facilityId',
											op: 'equals',
											value: selectedFacility?.id,
										},
									]);
								}}
								size='small'
							/>
						</div>
						<Button
							loading={activateSelectedParkersLoading}
							disabled={
								!activateParkersFacility.id ||
								!parkerRows?.length ||
								!selectedParkerIds.length ||
								waitlistParkersLoading
							}
							onClick={() => handleActionSubmitClick()}
							variant='contained'
							data-cy='activate-parkers-button'
							style={{ height: '35px' }}
						>
							Activate Selected Parkers By Facility
						</Button>
					</>
				);
			}
			if (!subIdToUse && bulkParkerActionName === 'Remove Waitlist Parkers By Facility') {
				return (
					<>
						<div style={{ width: '25%' }}>
							<FacilityAutocomplete
								facilityName={activateParkersFacility.facilityName}
								facilityNumber={activateParkersFacility.facilityNumber}
								facilityId={activateParkersFacility.id}
								organizationId={0}
								onChange={(selectedFacility) => {
									setActivateParkersFacility({
										id: selectedFacility?.id ?? '',
										facilityName: selectedFacility?.facilityName ?? '',
										facilityNumber: selectedFacility?.facilityNumber?.toString(),
									});

									debouncedSetFilters([
										...filters,
										{
											propertyName: 'facilityId',
											op: 'equals',
											value: selectedFacility?.id,
										},
									]);
								}}
								size='small'
							/>
						</div>
						<Button
							loading={deletePendingParkersBySubscriptionLoading}
							disabled={
								!activateParkersFacility.id ||
								!parkerRows?.length ||
								!selectedParkerIds.length ||
								waitlistParkersLoading
							}
							onClick={() => handleActionSubmitClick()}
							variant='contained'
							data-cy='activate-parkers-button'
							style={{ height: '35px' }}
						>
							Remove Selected Parkers By Facility
						</Button>
					</>
				);
			}

			return null;
		};

		const waitlistParkerOptions = subIdToUse
			? [
					{ label: 'Activate Waitlist Parkers', value: 'ACTIVATE' },
					{ label: 'Remove Waitlist Parkers', value: 'REMOVE' },
				]
			: [
					{ label: 'Activate Waitlist Parkers By Facility', value: 'ACTIVATE_BY_FACILITY' },
					{ label: 'Remove Waitlist Parkers By Facility', value: 'REMOVE_BY_FACILITY' },
				];

		return (
			<GridToolbarContainer>
				<GridToolbarFilterButton data-cy='filter-table-button' />
				<GridToolbarColumnsButton />
				<GridToolbarDensitySelector />
				{csvExportButton ? (
					<CsvExportButtonV2
						query={GET_WAITLIST_PARKERS_CSV}
						queryName='getWaitlistParkersCSV'
						args={{
							variables: {
								subscriptionId,
								order: sortOrder,
								filters,
							},
							context: {
								apiVersion: 'v2',
							},
						}}
					/>
				) : null}
				{!isOwnerPortal && (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<FormControl variant='outlined' size='small' style={{ marginRight: '10px' }}>
							<InputLabel id='bulk-action-label'>Action</InputLabel>
							<Select
								labelId='bulk-action-label'
								id='bulk-action-select'
								label='Action'
								value={bulkParkerActionName || ''}
								onChange={handleBulkActionChange}
								style={{ height: '35px', width: '400px', padding: '0px 10px' }}
								inputProps={{ 'data-cy': 'parker-bulk-actions-select' }}
							>
								{waitlistParkerOptions.map((option) => (
									<MenuItem key={option.value} value={option.label}>
										{option.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						{renderCorrectInputsFromAction()}
					</div>
				)}
			</GridToolbarContainer>
		);
	}, [
		subIdToUse,
		csvExportButton,
		subscriptionId,
		sortOrder,
		filters,
		bulkParkerActionName,
		parkerRows,
		selectedParkerIds,
		setShowDialogForParkersTable,
		theme,
		usesDefaultRates,
		getStartDateAndPriceError,
		setShowDialog,
		setShowSnackbar,
		activateSelectedParkers,
		deletePendingParkersBySubscription,
		tenantSubscriptionId,
		refetch,
		activateSelectedParkersLoading,
		deletePendingParkersBySubscriptionLoading,
		activateParkersFacility.facilityName,
		activateParkersFacility.facilityNumber,
		activateParkersFacility.id,
		waitlistParkersLoading,
		debouncedSetFilters,
	]);

	const onSortModelChange = useCallback(
		(sortModel) => {
			OnSortModelChange({ setPaginationModel, setSortOrder })(sortModel);
		},
		[setPaginationModel, setSortOrder],
	);

	const onFilterModelChange = useCallback(
		(filterModel) => {
			OnFilterModelChange({ setFilters: debouncedSetFilters, setPaginationModel })(filterModel);
		},
		[debouncedSetFilters, setPaginationModel],
	);

	const isActivateOrRemoveByFacility =
		bulkParkerActionName === 'Activate Waitlist Parkers By Facility' ||
		bulkParkerActionName === 'Remove Waitlist Parkers By Facility';

	const hasFacilitySelected = Object.keys(activateParkersFacility ?? {}).length > 0;

	return (
		<div className='pca__datagrid-sticky--headfoot' data-cy='parkers-table-container'>
			<DataGridPremium
				checkboxSelection={!!bulkParkerActionName && (!isActivateOrRemoveByFacility || hasFacilitySelected)}
				paginationModel={{ page, pageSize }}
				onSortModelChange={onSortModelChange}
				onFilterModelChange={onFilterModelChange}
				hideFooter={waitlistParkersLoading}
				onPaginationModelChange={setPaginationModel}
				pagination
				rows={parkerRows || []}
				rowCount={rowCount}
				pageSizeOptions={[pageSize]}
				columns={columns}
				paginationMode='server'
				sortingMode='server'
				filterMode='server'
				loading={waitlistParkersLoading}
				slots={{
					toolbar: CustomToolbar,
					loadingOverlay: DatagridLinearProgress,
				}}
				slotProps={{
					filterPanel: { ...datagridFilterPanelAttributes },
				}}
				rowSelectionModel={selectedParkerIds}
				getRowClassName={(params) => (parkersWithErrors.includes(params.id) ? 'red-row' : '')}
				onRowSelectionModelChange={(ids) => setSelectedParkerIds(ids)}
				initialState={{
					filter: {
						filterModel: {
							items: [],
						},
					},
				}}
				sx={{
					'& .red-row': {
						backgroundColor: 'rgba(255, 99, 71, .9) !important',
					},
					height: '100%',
				}}
			/>
			{!showSnackbar && (
				<Snackbar showSnackbar={showSnackbarForParkersTable} setShowSnackbar={setShowSnackbarForParkersTable} />
			)}
			{!showDialog && (
				<Dialog showDialog={showDialogForParkersTable} setShowDialog={setShowDialogForParkersTable} />
			)}
		</div>
	);
};
